import React, { useMemo, useState } from "react";
import SalesTable from "../../InventoryManagement/SalesInvoice/SalesTable";
import Swal from "sweetalert2";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import { toast, ToastContainer } from "react-toastify";
import useFormattedDate from "../../../../../context/useFormattingUtils";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import POSThermalInvoiceModal from "./POSThermalInvoiceModal";

export default function POSInvoiceHistory() {
  const { Delete, Post, GetPaginatedData } = useApi();

  const [showInvoice, setShowInvoice] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [posInvoiceData, setPosInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [exportConfig, setExportConfig] = useState({
    datePreset: "this_month",
    fromDate: "",
    toDate: "",
    receiptColumns: [
      "invoice_no",
      "date",
      "pay_method",
      "line_count",
      "total_qty",
      "payable_amount",
    ],
    itemColumns: ["item_name", "item_qty", "item_rate", "item_total"],
  });

  const { formatDate } = useFormattedDate();
  const { formatCurrency } = useFormattingUtils();

  const safeCurrency = (value) => {
    const amount = parseFloat(value || 0);
    if (Number.isNaN(amount)) return formatCurrency ? formatCurrency(0) : "0.00";
    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const safeNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 0 : number;
  };

  const getPaymentMethod = (value) => {
    const method = String(value || "N/A").trim();
    return method || "N/A";
  };

  const formatDateInput = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
  };

  const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
  };

  const addMonths = (date, amount) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + amount);
    return next;
  };

  const getDateRangeForPreset = (preset) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    if (preset === "all") {
      return { from: null, to: null, label: "All dates" };
    }

    if (preset === "this_month") {
      return {
        from: startOfDay(new Date(currentYear, currentMonth, 1)),
        to: endOfDay(new Date(currentYear, currentMonth + 1, 0)),
        label: "This month",
      };
    }

    if (preset === "last_month") {
      return {
        from: startOfDay(new Date(currentYear, currentMonth - 1, 1)),
        to: endOfDay(new Date(currentYear, currentMonth, 0)),
        label: "Last month",
      };
    }

    if (preset === "this_quarter") {
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      return {
        from: startOfDay(new Date(currentYear, quarterStartMonth, 1)),
        to: endOfDay(new Date(currentYear, quarterStartMonth + 3, 0)),
        label: "This quarter",
      };
    }

    if (preset === "last_6_months") {
      return {
        from: startOfDay(addMonths(today, -6)),
        to: endOfDay(today),
        label: "Last 6 months",
      };
    }

    if (preset === "financial_year") {
      const fiscalStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
      return {
        from: startOfDay(new Date(fiscalStartYear, 3, 1)),
        to: endOfDay(new Date(fiscalStartYear + 1, 2, 31)),
        label: "This financial year",
      };
    }

    if (preset === "custom") {
      const fromDate = parseDateValue(exportConfig.fromDate);
      const toDate = parseDateValue(exportConfig.toDate);

      return {
        from: fromDate ? startOfDay(fromDate) : null,
        to: toDate ? endOfDay(toDate) : null,
        label: "Custom range",
      };
    }

    return {
      from: startOfDay(new Date(currentYear, currentMonth, 1)),
      to: endOfDay(new Date(currentYear, currentMonth + 1, 0)),
      label: "This month",
    };
  };

  const getInvoiceItems = (invoice) => {
    const candidates = [
      invoice?.items,
      invoice?.products,
      invoice?.invoice_products,
      invoice?.pos_invoice_products,
      invoice?.sales_invoice_products,
      invoice?.product_details,
      invoice?.details,
    ];

    const found = candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0);
    return Array.isArray(found) ? found : [];
  };

  const getItemName = (item) =>
    item?.pos_item_title ||
    item?.product_title ||
    item?.title ||
    item?.name ||
    item?.product_name ||
    item?.product?.title ||
    item?.product?.name ||
    "Item";

  const getItemQty = (item) =>
    safeNumber(item?.qty || item?.quantity || item?.sold_qty_stock || item?.total_qty || 0);

  const getItemRate = (item) =>
    safeNumber(
      item?.amount_per_item ||
        item?.rate ||
        item?.price ||
        item?.selling_price ||
        item?.amount ||
        0
    );

  const getItemTotal = (item) => {
    const explicitTotal = safeNumber(
      item?.total_amount ||
        item?.total ||
        item?.after_discount ||
        item?.line_total ||
        0
    );

    if (explicitTotal !== 0) return explicitTotal;
    return getItemQty(item) * getItemRate(item);
  };

  const getInvoiceItemCount = (invoice) => {
    const items = getInvoiceItems(invoice);
    if (items.length > 0) return items.length;
    return safeNumber(invoice?.total_qty || invoice?.qty || 0);
  };

  const getInvoiceQty = (invoice) => {
    const items = getInvoiceItems(invoice);
    if (items.length > 0) {
      return items.reduce((total, item) => total + getItemQty(item), 0);
    }

    return safeNumber(invoice?.total_qty || invoice?.qty || 0);
  };

  const getInvoiceDate = (invoice) => parseDateValue(invoice?.raw_date || invoice?.date);

  const isInvoiceInExportRange = (invoice, range) => {
    const invoiceDate = getInvoiceDate(invoice);
    if (!invoiceDate) return !range.from && !range.to;
    if (range.from && invoiceDate < range.from) return false;
    if (range.to && invoiceDate > range.to) return false;
    return true;
  };

  const getInvoiceSubtotal = (invoice) => {
    const items = getInvoiceItems(invoice);
    const itemSubtotal = items.reduce((total, item) => total + getItemTotal(item), 0);
    return itemSubtotal || safeNumber(invoice?.subtotal || invoice?.total_amount || invoice?.payable_amount);
  };

  const receiptExportColumns = [
    { key: "invoice_no", label: "Receipt No", getValue: (invoice) => invoice.invoice_no || "" },
    {
      key: "date",
      label: "Billing Date",
      getValue: (invoice) => invoice.raw_date || invoice.date || "",
    },
    { key: "pay_method", label: "Payment Method", getValue: (invoice) => getPaymentMethod(invoice.pay_method) },
    { key: "line_count", label: "Line Count", getValue: (invoice) => getInvoiceItemCount(invoice) },
    { key: "total_qty", label: "Total Qty", getValue: (invoice) => getInvoiceQty(invoice) },
    { key: "subtotal", label: "Subtotal", getValue: (invoice) => getInvoiceSubtotal(invoice).toFixed(2) },
    {
      key: "discount",
      label: "Discount",
      getValue: (invoice) =>
        safeNumber(invoice.discount || invoice.discount_amount || invoice.additional_discount_value).toFixed(2),
    },
    {
      key: "round_off",
      label: "Round Off",
      getValue: (invoice) => safeNumber(invoice.round_off || invoice.roundoff).toFixed(2),
    },
    {
      key: "payable_amount",
      label: "Collected Amount",
      getValue: (invoice) => safeNumber(invoice.payable_amount || invoice.total_amount).toFixed(2),
    },
  ];

  const itemExportColumns = [
    { key: "item_name", label: "Item Name", getValue: (_invoice, item) => (item ? getItemName(item) : "") },
    { key: "item_qty", label: "Item Qty", getValue: (_invoice, item) => (item ? getItemQty(item) : "") },
    { key: "item_rate", label: "Item Rate", getValue: (_invoice, item) => (item ? getItemRate(item).toFixed(2) : "") },
    { key: "item_total", label: "Item Total", getValue: (_invoice, item) => (item ? getItemTotal(item).toFixed(2) : "") },
    { key: "item_uom", label: "Item UOM", getValue: (_invoice, item) => item?.uom_title || item?.uom || "" },
    { key: "item_barcode", label: "Item Barcode/SKU", getValue: (_invoice, item) => item?.barcode || item?.sku || "" },
  ];

  const datePresetOptions = [
    { value: "this_month", label: "This month" },
    { value: "last_month", label: "Last month" },
    { value: "this_quarter", label: "This quarter" },
    { value: "last_6_months", label: "Last 6 months" },
    { value: "financial_year", label: "This financial year" },
    { value: "all", label: "All dates" },
    { value: "custom", label: "Custom range" },
  ];

  const toggleExportColumn = (group, key) => {
    const groupKey = group === "item" ? "itemColumns" : "receiptColumns";
    setExportConfig((prev) => {
      const current = prev[groupKey] || [];
      const next = current.includes(key)
        ? current.filter((columnKey) => columnKey !== key)
        : [...current, key];

      return { ...prev, [groupKey]: next };
    });
  };

  const updateDatePreset = (datePreset) => {
    const range = getDateRangeForPreset(datePreset);
    setExportConfig((prev) => ({
      ...prev,
      datePreset,
      fromDate: datePreset === "custom" ? prev.fromDate : formatDateInput(range.from),
      toDate: datePreset === "custom" ? prev.toDate : formatDateInput(range.to),
    }));
  };

  const csvEscape = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsvFile = (filename, rows) => {
    const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchAllInvoicesForExport = async () => {
    const pageSize = 100;
    const trimmedSearchTerm = String(searchQuery || "").trim();
    const firstResponse = await GetPaginatedData("getProductPosInvoice", {
      page: 1,
      search: trimmedSearchTerm || undefined,
      queryParams: { page_size: pageSize },
    });

    const firstData = Array.isArray(firstResponse?.data) ? firstResponse.data : [];
    const totalPages = Math.ceil((firstResponse?.totalCount || firstData.length || 0) / pageSize) || 1;
    const allRows = [...firstData];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await GetPaginatedData("getProductPosInvoice", {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: { page_size: pageSize },
      });

      if (Array.isArray(response?.data)) {
        allRows.push(...response.data);
      }
    }

    return allRows.map((item) => ({
      ...item,
      date: item.date ? formatDate(item.date) : "N/A",
      raw_date: item.date,
    }));
  };

  const handleGenerateCsv = async () => {
    const selectedReceiptColumns = receiptExportColumns.filter((column) =>
      exportConfig.receiptColumns.includes(column.key)
    );
    const selectedItemColumns = itemExportColumns.filter((column) =>
      exportConfig.itemColumns.includes(column.key)
    );

    if (selectedReceiptColumns.length === 0 && selectedItemColumns.length === 0) {
      toast.error("Please select at least one column for export.");
      return;
    }

    const range = getDateRangeForPreset(exportConfig.datePreset);
    if (exportConfig.datePreset === "custom" && (!range.from || !range.to)) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (range.from && range.to && range.from > range.to) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    try {
      setExportLoading(true);
      const invoices = await fetchAllInvoicesForExport();
      const filteredInvoices = invoices.filter((invoice) => isInvoiceInExportRange(invoice, range));

      if (filteredInvoices.length === 0) {
        toast.info("No POS receipts found for the selected export range.");
        return;
      }

      const header = [
        ...selectedReceiptColumns.map((column) => column.label),
        ...selectedItemColumns.map((column) => column.label),
      ];

      const includeItemRows = selectedItemColumns.length > 0;
      const bodyRows = filteredInvoices.flatMap((invoice) => {
        const items = getInvoiceItems(invoice);
        const rowsToBuild = includeItemRows && items.length > 0 ? items : [null];

        return rowsToBuild.map((item) => [
          ...selectedReceiptColumns.map((column) => column.getValue(invoice, item)),
          ...selectedItemColumns.map((column) => column.getValue(invoice, item)),
        ]);
      });

      const rangeLabel = range.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      downloadCsvFile(`pos-receipts-${rangeLabel || "export"}.csv`, [header, ...bodyRows]);
      toast.success(`Exported ${bodyRows.length} CSV row${bodyRows.length === 1 ? "" : "s"}.`);
      setShowExportModal(false);
    } catch (error) {
      console.error("Error exporting POS receipts:", error);
      toast.error("Failed to generate CSV export.");
    } finally {
      setExportLoading(false);
    }
  };

  const fetchSalesInvoiceData = async ({
    page = 1,
    searchTerm = "",
    sort = "",
    order = "",
  } = {}) => {
    const trimmedSearchTerm = String(searchTerm || searchQuery || "").trim();

    try {
      setLoading(true);

      const response = await GetPaginatedData("getProductPosInvoice", {
        page,
        search: trimmedSearchTerm || undefined,
        sort,
        order,
      });

      const results = Array.isArray(response?.data) ? response.data : [];
      const totalPages = Math.ceil((response?.totalCount || 0) / 10) || 1;

      const formatted = results.map((item) => ({
        ...item,
        date: item.date ? formatDate(item.date) : "N/A",
        raw_date: item.date,
      }));

      setPosInvoiceData(formatted);

      return { data: formatted, totalPages };
    } catch (error) {
      console.error("Error fetching POS invoice data:", error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const fetchPOSDataForTable = async ({ page = 1, searchTerm = "" }) => {
    return await fetchSalesInvoiceData({
      page,
      searchTerm: searchTerm || searchQuery,
    });
  };

  const handleSearchSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    setTableRefreshKey((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setTableRefreshKey((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setTableRefreshKey((prev) => prev + 1);
  };

  const handlePrint = async (invoiceId, openModal = false) => {
    try {
      setLoading(true);

      const response = await Post("getSalesInvoiceDetails", {
        invoice_id: invoiceId,
      });

      if (response?.data && openModal) {
        setModalData(response.data);
        setShowInvoice(true);
      }
    } catch (error) {
      console.error("Error fetching POS invoice details:", error);
      toast.error("Failed to open POS receipt preview.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowInvoice(false);
    setModalData(null);
  };

  const handleDeleteProduct = async (invoiceId) => {
    const result = await Swal.fire({
      title: "Delete POS receipt?",
      text: "This receipt history will be removed. This action cannot be reverted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#111827",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await Delete("salesInvoiceData", invoiceId);
      toast.success("POS receipt deleted successfully.");
      setTableRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting POS invoice:", error);
      toast.error("Failed to delete POS receipt.");
    }
  };

  const stats = useMemo(() => {
    const receiptCount = posInvoiceData.length;

    const totalCollected = posInvoiceData.reduce(
      (total, invoice) => total + (parseFloat(invoice.payable_amount) || 0),
      0
    );

    const cashCollected = posInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() === "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const otherCollected = posInvoiceData
      .filter((invoice) => getPaymentMethod(invoice.pay_method).toLowerCase() !== "cash")
      .reduce((total, invoice) => total + (parseFloat(invoice.payable_amount) || 0), 0);

    const averageReceipt = receiptCount > 0 ? totalCollected / receiptCount : 0;

    return {
      receiptCount,
      totalCollected,
      cashCollected,
      otherCollected,
      averageReceipt,
    };
  }, [posInvoiceData]);

  const columns = [
    {
      field: "invoice_no",
      header: "Receipt No",
      body: (rowData) => (
        <div className="posh-receipt-cell">
          <div className="posh-receipt-no">{rowData.invoice_no || "-"}</div>
          <div className="posh-receipt-subtitle">POS counter receipt</div>
        </div>
      ),
    },
    {
      field: "total_qty",
      header: "Items / Qty",
      body: (rowData) => (
        <div className="posh-items-cell">
          <span className="posh-item-count">{getInvoiceItemCount(rowData)} lines</span>
          <span className="posh-qty-muted">{getInvoiceQty(rowData)} qty</span>
        </div>
      ),
    },
    {
      field: "payable_amount",
      header: "Collected",
      body: (rowData) => (
        <span className="posh-money-pill">
          {safeCurrency(rowData.payable_amount)}
        </span>
      ),
    },
    {
      field: "pay_method",
      header: "Payment",
      body: (rowData) => {
        const method = getPaymentMethod(rowData.pay_method);
        const isCash = method.toLowerCase() === "cash";

        return (
          <span className={isCash ? "posh-pay-pill posh-pay-cash" : "posh-pay-pill posh-pay-other"}>
            {method}
          </span>
        );
      },
    },
    {
      field: "date",
      header: "Billing Date",
      body: (rowData) => (
        <div className="posh-date-cell">
          {rowData.raw_date ? formatDate(rowData.raw_date) : rowData.date || "N/A"}
        </div>
      ),
    },
  ];

  const renderExpandedReceipt = (invoice) => {
    const items = getInvoiceItems(invoice);
    const itemSubtotal = items.reduce((total, item) => total + getItemTotal(item), 0);
    const payable = safeNumber(invoice.payable_amount || invoice.total_amount || itemSubtotal);
    const discount = safeNumber(invoice.discount || invoice.discount_amount || invoice.additional_discount_value);
    const roundOff = safeNumber(invoice.round_off || invoice.roundoff);

    return (
      <div className="posh-expanded-card">
        <div className="posh-expanded-head">
          <div>
            <div className="posh-expanded-title">Receipt Items</div>
            <div className="posh-expanded-subtitle">
              {invoice.invoice_no || "Receipt"} - {getPaymentMethod(invoice.pay_method)} - {invoice.raw_date ? formatDate(invoice.raw_date) : invoice.date || "N/A"}
            </div>
          </div>
          <div className="posh-expanded-total">
            <span>Total</span>
            <strong>{safeCurrency(payable)}</strong>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="posh-items-table-wrap">
            <table className="posh-items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id || `${getItemName(item)}-${index}`}>
                    <td>
                      <div className="posh-line-item-name">{getItemName(item)}</div>
                      {(item.uom_title || item.barcode || item.sku) && (
                        <div className="posh-line-item-meta">
                          {[item.uom_title, item.barcode || item.sku].filter(Boolean).join(" - ")}
                        </div>
                      )}
                    </td>
                    <td>{getItemQty(item)}</td>
                    <td>{safeCurrency(getItemRate(item))}</td>
                    <td>
                      <strong>{safeCurrency(getItemTotal(item))}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="posh-expanded-empty">
            No item-level details were returned for this receipt.
          </div>
        )}

        <div className="posh-expanded-summary">
          <div>
            <span>Line subtotal</span>
            <strong>{safeCurrency(itemSubtotal || payable)}</strong>
          </div>
          <div>
            <span>Total quantity</span>
            <strong>{getInvoiceQty(invoice)}</strong>
          </div>
          {discount !== 0 && (
            <div>
              <span>Discount</span>
              <strong>{safeCurrency(discount)}</strong>
            </div>
          )}
          {roundOff !== 0 && (
            <div>
              <span>Round off</span>
              <strong>{safeCurrency(roundOff)}</strong>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStatCard = ({ title, value, subtitle, icon, tone = "default" }) => (
    <div className={`posh-stat-card posh-stat-${tone}`}>
      <div className="posh-stat-topline">
        <span className="posh-stat-icon">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="posh-stat-value">{value}</div>
      {subtitle && <div className="posh-stat-subtitle">{subtitle}</div>}
    </div>
  );

  const exportRangePreview = getDateRangeForPreset(exportConfig.datePreset);

  return (
    <div>
      <ToastContainer />

      <style>
        {`
          .posh-page {
            background:
              radial-gradient(circle at top left, rgba(59, 130, 246, 0.12), transparent 36%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            min-height: 100vh;
            padding: 6px 0 24px;
          }

          .posh-hero {
            background:
              linear-gradient(135deg, #111827 0%, #1e293b 58%, #334155 100%);
            border-radius: 26px;
            padding: 26px;
            color: #ffffff;
            margin-bottom: 18px;
            box-shadow: 0 22px 50px rgba(15, 23, 42, 0.23);
            position: relative;
            overflow: hidden;
          }

          .posh-hero::after {
            content: "";
            position: absolute;
            width: 270px;
            height: 270px;
            right: -90px;
            top: -110px;
            border-radius: 50%;
            background: rgba(56, 189, 248, 0.18);
          }

          .posh-hero-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 22px;
            flex-wrap: wrap;
          }

          .posh-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(56, 189, 248, 0.14);
            border: 1px solid rgba(125, 211, 252, 0.32);
            color: #bae6fd;
            padding: 8px 13px;
            border-radius: 999px;
            font-weight: 950;
            font-size: 12px;
            margin-bottom: 13px;
          }

          .posh-title {
            margin: 0;
            font-size: 31px;
            font-weight: 950;
            letter-spacing: -0.04em;
            line-height: 1.08;
          }

          .posh-description {
            margin: 9px 0 0;
            color: rgba(255, 255, 255, 0.82);
            font-size: 14px;
            max-width: 650px;
            line-height: 1.55;
          }

          .posh-hero-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .posh-primary-btn,
          .posh-secondary-btn,
          .posh-light-btn,
          .posh-export-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 950;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
            white-space: nowrap;
          }

          .posh-primary-btn:hover,
          .posh-secondary-btn:hover,
          .posh-light-btn:hover,
          .posh-export-btn:hover {
            transform: translateY(-1px);
          }

          .posh-light-btn {
            background: #ffffff;
            color: #111827;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
          }

          .posh-secondary-btn {
            background: rgba(255, 255, 255, 0.12);
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.26);
          }

          .posh-primary-btn {
            background: #2563eb;
            color: #ffffff;
          }

          .posh-export-btn {
            background: #0f172a;
            color: #ffffff;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.15);
          }

          .posh-btn-disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .posh-stats-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 18px;
          }

          .posh-stat-card {
            background: rgba(255, 255, 255, 0.94);
            border: 1px solid #e5e7eb;
            border-radius: 20px;
            padding: 17px 18px;
            box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
            backdrop-filter: blur(8px);
          }

          .posh-stat-collected {
            background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
            border-color: #bfdbfe;
          }

          .posh-stat-cash {
            background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
            border-color: #bbf7d0;
          }

          .posh-stat-other {
            background: linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%);
            border-color: #ddd6fe;
          }

          .posh-stat-average {
            background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
            border-color: #fed7aa;
          }

          .posh-stat-topline {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.055em;
          }

          .posh-stat-icon {
            width: 28px;
            height: 28px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            font-size: 15px;
          }

          .posh-stat-value {
            margin-top: 10px;
            font-size: 25px;
            font-weight: 950;
            color: #0f172a;
            line-height: 1;
            word-break: break-word;
          }

          .posh-stat-subtitle {
            margin-top: 8px;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .posh-table-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 24px;
            padding: 18px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
          }

          .posh-table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .posh-section-title {
            margin: 0;
            font-size: 21px;
            font-weight: 950;
            color: #0f172a;
            letter-spacing: -0.02em;
          }

          .posh-section-subtitle {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posh-search-form {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .posh-search-wrap {
            position: relative;
          }

          .posh-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #94a3b8;
            font-size: 14px;
          }

          .posh-search-input {
            width: 285px;
            height: 42px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            padding: 0 16px 0 38px;
            font-size: 13px;
            outline: none;
            font-weight: 750;
            color: #111827;
            background: #ffffff;
            transition: border 0.2s ease, box-shadow 0.2s ease;
          }

          .posh-search-input:focus {
            border-color: #60a5fa;
            box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.16);
          }

          .posh-table-shell {
            border: 1px solid #e5e7eb;
            border-radius: 18px;
            overflow: hidden;
            background: #ffffff;
          }

          .posh-receipt-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .posh-receipt-no {
            font-weight: 950;
            color: #0f172a;
          }

          .posh-receipt-subtitle {
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
          }

          .posh-money-pill {
            display: inline-flex;
            background: #eff6ff;
            color: #1d4ed8;
            border: 1px solid #bfdbfe;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
          }

          .posh-pay-pill {
            display: inline-flex;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            text-transform: capitalize;
            white-space: nowrap;
          }

          .posh-pay-cash {
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
          }

          .posh-pay-other {
            background: #f5f3ff;
            color: #6d28d9;
            border: 1px solid #ddd6fe;
          }

          .posh-date-cell {
            font-weight: 850;
            color: #0f172a;
            white-space: nowrap;
          }

          .expandable-table-row:hover {
            background: #f8fafc;
          }

          .expand-row-button {
            width: 28px;
            height: 28px;
            border: 1px solid #dbeafe;
            border-radius: 999px;
            background: #eff6ff;
            color: #1d4ed8;
            font-size: 17px;
            line-height: 1;
            font-weight: 950;
            cursor: pointer;
          }

          .expanded-table-detail-row td {
            background: #f8fafc;
            padding: 0 !important;
            border-top: 1px solid #e5e7eb;
          }

          .posh-items-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .posh-item-count {
            font-weight: 950;
            color: #0f172a;
            white-space: nowrap;
          }

          .posh-qty-muted {
            font-size: 11px;
            color: #64748b;
            font-weight: 800;
            white-space: nowrap;
          }

          .posh-expanded-card {
            padding: 16px;
            background:
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          }

          .posh-expanded-head {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 13px;
            flex-wrap: wrap;
          }

          .posh-expanded-title {
            font-size: 15px;
            font-weight: 950;
            color: #0f172a;
          }

          .posh-expanded-subtitle {
            margin-top: 3px;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posh-expanded-total {
            display: flex;
            flex-direction: column;
            gap: 3px;
            align-items: flex-end;
            background: #ecfdf5;
            border: 1px solid #bbf7d0;
            border-radius: 14px;
            padding: 9px 12px;
            color: #047857;
          }

          .posh-expanded-total span {
            font-size: 11px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.04em;
          }

          .posh-expanded-total strong {
            font-size: 15px;
            font-weight: 950;
          }

          .posh-items-table-wrap {
            overflow-x: auto;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
            background: #ffffff;
          }

          .posh-items-table {
            width: 100%;
            border-collapse: collapse;
            min-width: 560px;
          }

          .posh-items-table th {
            background: #f1f5f9;
            color: #475569;
            font-size: 11px;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            padding: 10px 12px;
            text-align: left;
          }

          .posh-items-table td {
            padding: 11px 12px !important;
            border-top: 1px solid #e5e7eb;
            color: #0f172a;
            font-size: 13px;
            font-weight: 800;
            background: #ffffff !important;
          }

          .posh-items-table th:nth-child(n+2),
          .posh-items-table td:nth-child(n+2) {
            text-align: right;
            white-space: nowrap;
          }

          .posh-line-item-name {
            font-weight: 950;
            color: #0f172a;
          }

          .posh-line-item-meta {
            margin-top: 2px;
            color: #64748b;
            font-size: 11px;
            font-weight: 750;
          }

          .posh-expanded-empty {
            border: 1px dashed #cbd5e1;
            border-radius: 14px;
            padding: 16px;
            color: #64748b;
            font-size: 13px;
            font-weight: 800;
            background: #ffffff;
          }

          .posh-expanded-summary {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            margin-top: 12px;
          }

          .posh-expanded-summary div {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 10px 12px;
            display: flex;
            justify-content: space-between;
            gap: 10px;
            color: #0f172a;
            font-size: 12px;
            font-weight: 900;
          }

          .posh-expanded-summary span {
            color: #64748b;
            font-weight: 800;
          }

          .posh-export-overlay {
            position: fixed;
            inset: 0;
            z-index: 1200;
            background: rgba(15, 23, 42, 0.58);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
          }

          .posh-export-modal {
            width: min(960px, 96vw);
            max-height: 92vh;
            overflow: auto;
            background: #ffffff;
            border-radius: 22px;
            box-shadow: 0 28px 70px rgba(15, 23, 42, 0.36);
            border: 1px solid #e5e7eb;
          }

          .posh-export-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 14px;
            padding: 18px 20px;
            border-bottom: 1px solid #e5e7eb;
            position: sticky;
            top: 0;
            background: #ffffff;
            z-index: 2;
          }

          .posh-export-title {
            margin: 0;
            color: #0f172a;
            font-size: 20px;
            font-weight: 950;
          }

          .posh-export-subtitle {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posh-export-close {
            width: 34px;
            height: 34px;
            border: none;
            border-radius: 999px;
            background: #f1f5f9;
            color: #0f172a;
            font-size: 20px;
            font-weight: 900;
            cursor: pointer;
            line-height: 1;
          }

          .posh-export-body {
            padding: 20px;
            display: grid;
            gap: 18px;
          }

          .posh-export-section {
            border: 1px solid #e5e7eb;
            border-radius: 16px;
            padding: 15px;
            background: #f8fafc;
          }

          .posh-export-section-title {
            color: #0f172a;
            font-size: 14px;
            font-weight: 950;
            margin-bottom: 12px;
          }

          .posh-preset-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
          }

          .posh-preset-option {
            border: 1px solid #dbe3ef;
            border-radius: 12px;
            padding: 10px 12px;
            background: #ffffff;
            color: #334155;
            font-size: 12px;
            font-weight: 900;
            cursor: pointer;
            text-align: left;
          }

          .posh-preset-option.active {
            border-color: #2563eb;
            background: #eff6ff;
            color: #1d4ed8;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.09);
          }

          .posh-date-range-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-top: 12px;
          }

          .posh-export-field {
            display: flex;
            flex-direction: column;
            gap: 6px;
            color: #334155;
            font-size: 12px;
            font-weight: 900;
          }

          .posh-export-field input {
            height: 40px;
            border: 1px solid #d1d5db;
            border-radius: 10px;
            padding: 0 11px;
            color: #0f172a;
            font-size: 13px;
            font-weight: 800;
            background: #ffffff;
          }

          .posh-column-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
            gap: 10px;
          }

          .posh-column-option {
            display: flex;
            align-items: center;
            gap: 8px;
            border: 1px solid #dbe3ef;
            background: #ffffff;
            border-radius: 12px;
            padding: 10px 12px;
            color: #0f172a;
            font-size: 12px;
            font-weight: 850;
          }

          .posh-column-option input {
            accent-color: #2563eb;
          }

          .posh-export-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            padding: 16px 20px;
            border-top: 1px solid #e5e7eb;
            background: #ffffff;
            position: sticky;
            bottom: 0;
            flex-wrap: wrap;
          }

          .posh-export-hint {
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posh-export-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }

          @media (max-width: 1200px) {
            .posh-stats-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 768px) {
            .posh-page {
              padding: 0 0 18px;
            }

            .posh-hero {
              border-radius: 18px;
              padding: 20px;
            }

            .posh-title {
              font-size: 25px;
            }

            .posh-hero-actions,
            .posh-search-form {
              width: 100%;
              justify-content: stretch;
            }

            .posh-light-btn,
            .posh-secondary-btn,
            .posh-primary-btn {
              flex: 1;
            }

            .posh-stats-grid {
              grid-template-columns: 1fr;
            }

            .posh-table-card {
              padding: 14px;
              border-radius: 18px;
            }

            .posh-table-header {
              align-items: stretch;
            }

            .posh-search-wrap,
            .posh-search-input {
              width: 100%;
            }

            .posh-primary-btn,
            .posh-secondary-btn {
              width: 100%;
            }

            .posh-date-range-grid {
              grid-template-columns: 1fr;
            }

            .posh-export-footer,
            .posh-export-actions {
              align-items: stretch;
              width: 100%;
            }

            .posh-expanded-summary {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 480px) {
            .posh-stat-value {
              font-size: 22px;
            }

            .posh-table-shell {
              overflow-x: auto;
            }
          }
        `}
      </style>

      <div className="responsive-box posh-page">
        <div className="posh-hero">
          <div className="posh-hero-content">
            <div>
              <div className="posh-badge">
                <span>●</span>
                <span>POS Counter Receipts</span>
              </div>

              <h2 className="posh-title">POS Invoice History</h2>

              <p className="posh-description">
                Track quick counter-sale receipts, collected payments and thermal bill prints for POS transactions.
              </p>
            </div>

            <div className="posh-hero-actions">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className={`posh-light-btn ${loading ? "posh-btn-disabled" : ""}`}
              >
                {loading ? "Refreshing..." : "Refresh Receipts"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setTableRefreshKey((prev) => prev + 1);
                }}
                className="posh-secondary-btn"
              >
                Reset View
              </button>
            </div>
          </div>
        </div>

        <div className="posh-stats-grid">
          {renderStatCard({
            title: "Receipts Loaded",
            value: stats.receiptCount,
            subtitle: "Current POS result",
            icon: "🧾",
          })}

          {renderStatCard({
            title: "Total Collected",
            value: safeCurrency(stats.totalCollected),
            subtitle: "Money received at counter",
            icon: "₹",
            tone: "collected",
          })}

          {renderStatCard({
            title: "Cash Collected",
            value: safeCurrency(stats.cashCollected),
            subtitle: "Cash POS payments",
            icon: "💵",
            tone: "cash",
          })}

          {renderStatCard({
            title: "Average Receipt",
            value: safeCurrency(stats.averageReceipt),
            subtitle: "Per POS receipt",
            icon: "⚡",
            tone: "average",
          })}
        </div>

        <div className="posh-table-card">
          <div className="posh-table-header">
            <div>
              <h3 className="posh-section-title">POS Receipt List</h3>
              <p className="posh-section-subtitle">
                Search and print quick thermal receipts for POS product sales.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setTableRefreshKey((prev) => prev + 1);
              }}
              className="posh-search-form"
            >
              <div className="posh-search-wrap">
                <span className="posh-search-icon">⌕</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search receipt, customer, payment..."
                  className="posh-search-input"
                />
              </div>

              <button type="submit" className="posh-primary-btn">
                Search
              </button>

              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className="posh-export-btn"
              >
                Export CSV
              </button>

              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="posh-secondary-btn"
                  style={{
                    color: "#1d4ed8",
                    borderColor: "#bfdbfe",
                    background: "#eff6ff",
                  }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          <div className="posh-table-shell">
            <SalesTable
              key={`${tableRefreshKey}-${searchQuery}`}
              fetchData={fetchPOSDataForTable}
              columns={columns}
              showAction={true}
              loading={loading}
              onDelete={handleDeleteProduct}
              handlePrint={(invoiceId) => handlePrint(invoiceId, true)}
              enablePagination={true}
              renderExpandedRow={renderExpandedReceipt}
              showSearch={false}
            />
          </div>
        </div>

        {showExportModal && (
          <div className="posh-export-overlay" role="dialog" aria-modal="true">
            <div className="posh-export-modal">
              <div className="posh-export-header">
                <div>
                  <h3 className="posh-export-title">Custom CSV Export</h3>
                  <p className="posh-export-subtitle">
                    Choose the date range and exact fields for your POS receipt report.
                  </p>
                </div>
                <button
                  type="button"
                  className="posh-export-close"
                  onClick={() => setShowExportModal(false)}
                  aria-label="Close export modal"
                >
                  x
                </button>
              </div>

              <div className="posh-export-body">
                <div className="posh-export-section">
                  <div className="posh-export-section-title">Date Range</div>
                  <div className="posh-preset-grid">
                    {datePresetOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`posh-preset-option ${exportConfig.datePreset === option.value ? "active" : ""}`}
                        onClick={() => updateDatePreset(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  <div className="posh-date-range-grid">
                    <label className="posh-export-field">
                      <span>From date</span>
                      <input
                        type="date"
                        value={
                          exportConfig.datePreset === "custom"
                            ? exportConfig.fromDate
                            : formatDateInput(exportRangePreview.from)
                        }
                        disabled={exportConfig.datePreset !== "custom"}
                        onChange={(event) =>
                          setExportConfig((prev) => ({
                            ...prev,
                            datePreset: "custom",
                            fromDate: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label className="posh-export-field">
                      <span>To date</span>
                      <input
                        type="date"
                        value={
                          exportConfig.datePreset === "custom"
                            ? exportConfig.toDate
                            : formatDateInput(exportRangePreview.to)
                        }
                        disabled={exportConfig.datePreset !== "custom"}
                        onChange={(event) =>
                          setExportConfig((prev) => ({
                            ...prev,
                            datePreset: "custom",
                            toDate: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </div>

                <div className="posh-export-section">
                  <div className="posh-export-section-title">Receipt Columns</div>
                  <div className="posh-column-grid">
                    {receiptExportColumns.map((column) => (
                      <label className="posh-column-option" key={column.key}>
                        <input
                          type="checkbox"
                          checked={exportConfig.receiptColumns.includes(column.key)}
                          onChange={() => toggleExportColumn("receipt", column.key)}
                        />
                        <span>{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="posh-export-section">
                  <div className="posh-export-section-title">Item Columns</div>
                  <div className="posh-column-grid">
                    {itemExportColumns.map((column) => (
                      <label className="posh-column-option" key={column.key}>
                        <input
                          type="checkbox"
                          checked={exportConfig.itemColumns.includes(column.key)}
                          onChange={() => toggleExportColumn("item", column.key)}
                        />
                        <span>{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="posh-export-footer">
                <div className="posh-export-hint">
                  Item columns create one CSV row per receipt item. Without item columns, each receipt exports as one row.
                </div>
                <div className="posh-export-actions">
                  <button
                    type="button"
                    className="posh-secondary-btn"
                    style={{
                      color: "#334155",
                      borderColor: "#cbd5e1",
                      background: "#f8fafc",
                    }}
                    onClick={() => setShowExportModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`posh-primary-btn ${exportLoading ? "posh-btn-disabled" : ""}`}
                    disabled={exportLoading}
                    onClick={handleGenerateCsv}
                  >
                    {exportLoading ? "Generating..." : "Generate CSV"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showInvoice && (
          <POSThermalInvoiceModal
            open={showInvoice}
            onClose={handleCloseModal}
            data={modalData}
          />
        )}
      </div>
    </div>
  );
}
